import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Flex,
  Grid,
  GridItem,
  Image,
  Badge,
  useToast,
  Input,
  FormControl,
  FormLabel,
  Spinner,
  IconButton,
  useColorModeValue,
  Divider,
  Card,
  CardBody,
  CardHeader,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  CloseButton,
  Tag,
  TagLabel,
  TagLeftIcon,
  Select,
  Checkbox,
  useBoolean,
  ModalCloseButton
} from '@chakra-ui/react';
import { 
  FaUpload, 
  FaArrowLeft, 
  FaTrash, 
  FaImage, 
  FaCheck, 
  FaTimes, 
  FaUser, 
  FaCamera, 
  FaTag, 
  FaEdit,
  FaMagic
} from 'react-icons/fa';
import supabase from '../utils/supabaseUtils';
import { processAndUploadImage } from '../services/removeBgService';
import './UserImagesUpload.css';

const UserImagesUpload = () => {
  const { eventId, userId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const fileInputRef = useRef(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isTagModalOpen, setIsTagModalOpen] = useBoolean(false);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useBoolean(false);
  
  const [loading, setLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [event, setEvent] = useState(null);
  const [user, setUser] = useState(null);
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedTags, setSelectedTags] = useState([]);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [editingTags, setEditingTags] = useState([]);
  const [authInfo, setAuthInfo] = useState(null);
  const [approvalLink, setApprovalLink] = useState('');
  const [sendingApproval, setSendingApproval] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState({});
  const [isStaff, setIsStaff] = useState(false);
  const [stats, setStats] = useState({
    users: 0,
    events: 0,
    templates: 0,
    uploads: 0
  });
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headerBgColor = useColorModeValue('blue.500', 'blue.700');
  
  // Available tags for images
  const availableTags = [
    { id: 'player', label: 'Player', color: 'blue' },
    { id: 'action', label: 'Action Shot', color: 'orange' },
    { id: 'team', label: 'Team Photo', color: 'green' },
    { id: 'portrait', label: 'Portrait', color: 'purple' },
    { id: 'celebration', label: 'Celebration', color: 'yellow' },
    { id: 'crowd', label: 'Crowd', color: 'pink' },
    { id: 'equipment', label: 'Equipment', color: 'gray' },
    { id: 'venue', label: 'Venue', color: 'teal' }
  ];
  
  // Check authentication state
  useEffect(() => {
    // Debug log for authentication status
    const checkAuth = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Authentication error:', error);
          return;
        }
        
        if (data && data.session) {
          console.log('Authenticated as:', data.session.user.email);
          
          // Check user role using the RPC function
          const { data: userData, error: roleError } = await supabase
            .rpc('get_user_by_id', { user_id: data.session.user.id });
            
          if (roleError) {
            console.error('Error fetching role:', roleError);
          } else {
            console.log('User role:', userData?.user_type || 'unknown');
          }
          
          // Debug: Test direct read from user_uploads
          try {
            const { data: uploadTest, error: uploadError } = await supabase
              .from('user_uploads')
              .select('*', { count: 'exact', head: true });
              
            if (uploadError) {
              console.error('Cannot read from user_uploads:', uploadError);
            } else {
              console.log('Successfully read from user_uploads table, count:', 
                uploadTest && uploadTest.count !== undefined ? uploadTest.count : 'unknown');
            }
          } catch (e) {
            console.error('Error testing user_uploads access:', e);
          }
        } else {
          console.warn('No active session found');
        }
      } catch (e) {
        console.error('Error in auth check:', e);
      }
    };
    
    checkAuth();
  }, []);
  
  // Check if current user is staff or admin
  useEffect(() => {
    const checkUserRole = async () => {
      try {
        console.log("Checking user role...");
        
        const { data: session } = await supabase.auth.getSession();
        if (session && session.session) {
          console.log("Session found, user ID:", session.session.user.id);
          
          const { data: userData, error } = await supabase
            .from('app_users')
            .select('user_type, is_admin')
            .eq('id', session.session.user.id)
            .single();
            
          if (!error && userData) {
            // Debug log the user role information
            console.log("User role data:", userData);
            
            // Check for staff, OR is_admin flag
            const isStaffUser = userData.user_type === 'staff';
            const isAdminUser = userData.is_admin === true;
            
            console.log("User is staff:", isStaffUser);
            console.log("User is admin:", isAdminUser);
            
            // Set isStaff to true if either staff or admin
            setIsStaff(isStaffUser || isAdminUser);
            
            console.log("isStaff state set to:", isStaffUser || isAdminUser);
          } else {
            console.error("Error fetching user role:", error);
            console.error("Or no user data found");
          }
        } else {
          console.log("No active session found");
        }
      } catch (error) {
        console.error('Error checking user role:', error);
      }
    };
    
    checkUserRole();
  }, []);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchEventDetails(),
          fetchUserDetails(),
          fetchUserImages()
        ]);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        toast({
          title: 'Error loading data',
          description: 'Using demo mode',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        
        // Use demo data if fetching fails
        if (!event) {
          setEvent({
            id: eventId,
            name: 'Demo Event',
            start_date: new Date().toISOString().split('T')[0],
            end_date: new Date().toISOString().split('T')[0],
            status: 'active',
            city: 'Denver',
            state: 'CO'
          });
        }
        
        if (!user) {
          setUser({
            id: userId,
            first_name: 'Demo',
            last_name: 'User',
            email: 'demo@example.com',
            user_type: 'customer'
          });
        }
        
        if (images.length === 0) {
          // Generate some fake images for demo purposes
          const demoImages = [
            {
              id: 'demo-1',
              user_id: userId,
              event_id: eventId,
              image_url: 'https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?q=80&w=600',
              file_url: 'https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?q=80&w=600',
              status: 'active',
              tags: ['player', 'action'],
              created_at: new Date().toISOString()
            },
            {
              id: 'demo-2',
              user_id: userId,
              event_id: eventId,
              image_url: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?q=80&w=600',
              file_url: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?q=80&w=600',
              status: 'active',
              tags: ['team'],
              created_at: new Date().toISOString()
            }
          ];
          setImages(demoImages);
        }
        
        setLoading(false);
      }
    };
    
    fetchData();
  }, [eventId, userId]);
  
  const fetchEventDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single();
      
      if (error) throw error;
      
      if (data) {
        setEvent(data);
      } else {
        throw new Error('Event not found');
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
      throw error; // Propagate the error to be handled by the main useEffect
    }
  };
  
  const fetchUserDetails = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_user_by_id', { user_id: userId });
      
      if (error) {
        console.error('Error fetching user details:', error);
        console.log('Falling back to mock users data');
      }
      
      if (data) {
        setUser(data);
      } else {
        throw new Error('User not found');
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  };
  
  const fetchUserImages = async () => {
    try {
      // Fix the parameter name
      const uploadType = 'event_upload:' + eventId;
      
      // Before making an RPC call
      console.log('Calling function with params:', { param1: userId, param2: uploadType });
      
      // Add try/catch and log detailed errors
      try {
        const { data, error } = await supabase.rpc('get_user_uploads', { 
          user_id: userId,
          event_upload_type: uploadType
        });
        
        if (error) {
          console.error('RPC Error with get_user_uploads:', error);
          // Log the exact parameters that caused the error
          console.error('Parameters:', { param1: userId, param2: uploadType });
        } else {
          console.log('RPC Result:', data);
          
          if (data && Array.isArray(data)) {
            // Map data to expected format
            const formattedData = data.map(item => ({
              ...item,
              image_url: item.file_url, // Use file_url as image_url for display
              event_id: eventId // Add the event_id for component compatibility
            }));
            
            setImages(formattedData);
            console.log("Successfully fetched images:", formattedData.length);
          } else {
            console.log("No images found or data format unexpected:", data);
            setImages([]);
          }
        }
      } catch (e) {
        console.error('Exception in get_user_uploads call:', e);
      }
    } catch (error) {
      console.error('Error fetching user images:', error);
      throw error;
    }
  };
  
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Reset progress
    setUploadProgress(0);
    setUploadLoading(true);
    
    // Upload each file
    handleUpload(files);
  };
  
  const handleUpload = async (files) => {
    try {
      const totalFiles = files.length;
      let completedUploads = 0;
      let failedUploads = 0;
      const newImages = [];
      
      for (const file of files) {
        // Check file type
        if (!file.type.match('image.*')) {
          toast({
            title: 'Invalid file type',
            description: file.name + ' is not an image file',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          continue;
        }
        
        // Check file size (max 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
          toast({
            title: 'File too large',
            description: file.name + ' exceeds the maximum file size of 10MB',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          continue;
        }
        
        // Show processing status
        toast({
          title: 'Processing image',
          description: 'Removing background from ' + file.name + '...',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
        
        try {
          // Process and upload the image using remove.bg API
          let imageData = null;
          
          try {
            // Try with background removal first
            imageData = await processAndUploadImage(file, userId, eventId, selectedTags.length > 0 ? selectedTags : null);
          } catch (bgRemoveError) {
            console.error('Background removal error:', bgRemoveError);
            
            // Tell the user what happened
            toast({
              title: 'Background removal failed',
              description: 'Could not remove background from ' + file.name + '. Uploading original image.',
              status: 'warning',
              duration: 3000,
              isClosable: true,
            });
            
            // Upload the original image without background removal
            try {
              toast({
                title: 'Uploading original',
                description: 'Uploading ' + file.name + ' without background removal',
                status: 'info',
                duration: 3000,
                isClosable: true,
              });
              
              // Check authentication status
              const { data: { session }, error: authError } = await supabase.auth.getSession();
              if (authError || !session) {
                console.error('Authentication error:', authError || 'No session');
                throw new Error('Authentication required to upload images');
              }
              
              console.log('Uploading as authenticated user:', session.user.email);
              
              // Generate a unique filename
              const fileExt = file.name.split('.').pop();
              const fileName = userId + '_' + Date.now() + '_' + Math.random().toString(36).substring(2, 10) + '.' + fileExt;
              const filePath = 'events/' + eventId + '/users/' + userId + '/' + fileName;
              
              // Upload file to Supabase Storage
              const { data: uploadData, error: uploadError } = await supabase.storage
                .from('playmakerpics-images')
                .upload(filePath, file, {
                  cacheControl: '3600',
                  upsert: false,
                  contentType: file.type
                });
              
              if (uploadError) {
                throw new Error('Storage upload failed: ' + uploadError.message);
              }
              
              // Get the public URL for the uploaded file
              const { data: publicUrlData } = supabase.storage
                .from('playmakerpics-images')
                .getPublicUrl(filePath);
              
              const imageUrl = publicUrlData.publicUrl;
              
              console.log('Attempting to save upload via process_and_upload function...');
              const { data: dbData, error: dbError } = await supabase
                .rpc('process_and_upload', {
                  auth_user_id: session.user.id,  // Current authenticated user
                  target_user_id: userId,         // User we're uploading for
                  file_url: imageUrl,
                  upload_type: 'event_upload:' + eventId,
                  is_primary: false
                });
                
              if (dbError) {
                console.error('Upload function error:', dbError);
                throw dbError;
              }
              
              // Format the data for our component
              imageData = {
                id: dbData.id,
                user_id: userId,
                event_id: eventId,
                image_url: imageUrl,
                file_url: imageUrl,
                status: 'active',
                tags: selectedTags.length > 0 ? selectedTags : null,
                created_at: dbData.created_at,
                is_primary: dbData.is_primary,
                upload_type: 'event_upload:' + eventId
              };
            } catch (error) {
              console.error('Error uploading original image:', error);
              toast({
                title: 'Upload failed',
                description: 'Error uploading original ' + file.name + ': ' + error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
              });
              failedUploads++;
              continue;
            }
          }
          
          // If still no imageData, fallback to demo mode
          if (!imageData) {
            try {
              // Create a local URL from the original file
              const imageUrl = URL.createObjectURL(file);
              
              // Create a mock image record
              imageData = {
                id: 'local-' + Date.now() + '-' + Math.floor(Math.random() * 10000),
                user_id: userId,
                event_id: eventId, // Keep for component compatibility
                image_url: imageUrl,
                file_url: imageUrl,
                status: 'local',
                upload_type: 'event_upload:' + eventId,
                tags: selectedTags.length > 0 ? selectedTags : null,
                created_at: new Date().toISOString()
              };
              
              toast({
                title: 'Demo mode active',
                description: 'Image processed locally. The image is not saved to the database.',
                status: 'info',
                duration: 5000,
                isClosable: true,
              });
            } catch (processingError) {
              console.error('Processing error:', processingError);
              toast({
                title: 'Processing failed',
                description: 'Could not process ' + file.name + ': ' + processingError.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
              });
              failedUploads++;
              continue;
            }
          }
          
          // Add to our list of new images if we have data
          if (imageData) {
            newImages.push(imageData);
            
            // Update progress
            completedUploads++;
            setUploadProgress(Math.round((completedUploads / totalFiles) * 100));
          }
        } catch (error) {
          console.error('Error processing and uploading image:', error);
          toast({
            title: 'Processing failed',
            description: 'Error processing ' + file.name + ': ' + error.message,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          failedUploads++;
        }
      }
      
      // Update the images state with new uploads
      if (newImages.length > 0) {
        setImages(prev => [...newImages, ...prev]);
        setHasUploaded(true);
        
        toast({
          title: 'Upload successful',
          description: newImages.length + ' image' + (newImages.length !== 1 ? 's' : '') + ' uploaded and processed successfully' + (failedUploads > 0 ? ' (' + failedUploads + ' failed)' : ''),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else if (failedUploads > 0) {
        toast({
          title: 'Upload failed',
          description: 'All ' + failedUploads + ' image' + (failedUploads !== 1 ? 's' : '') + ' failed to process',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploadLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const handleDeleteImage = async (imageId) => {
    try {
      // Find the image to delete
      const imageToDelete = images.find(img => img.id === imageId);
      if (!imageToDelete) {
        toast({
          title: 'Error',
          description: 'Image not found',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      // If it's a local image (demo mode), just remove from state
      if (imageToDelete.status === 'local' || imageToDelete.id.startsWith('local-')) {
        if (imageToDelete.image_url && imageToDelete.image_url.startsWith('blob:')) {
          // Revoke the blob URL to prevent memory leaks
          URL.revokeObjectURL(imageToDelete.image_url);
        }
        
        // Update local state
        setImages(images.filter(img => img.id !== imageId));
        
        // If we're deleting the currently selected image, close the modal
        if (selectedImage && selectedImage.id === imageId) {
          setSelectedImage(null);
          onClose();
        }
        
        toast({
          title: 'Image deleted',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        return;
      }
      
      // Set to deleted status instead of actually deleting (soft delete)
      const { error: dbError } = await supabase
        .from('user_uploads')
        .update({ status: 'deleted' })
        .eq('id', imageId)
        .eq('user_id', userId); // Extra security to ensure ownership
        
      if (dbError) throw dbError;
      
      // Update local state
      setImages(images.filter(img => img.id !== imageId));
      
      // If we're deleting the currently selected image, close the modal
      if (selectedImage && selectedImage.id === imageId) {
        setSelectedImage(null);
        onClose();
      }
      
      toast({
        title: 'Image deleted',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
    } catch (error) {
      console.error('Error deleting image:', error);
      toast({
        title: 'Delete failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };
  
  const handleTagSelect = (tagId) => {
    if (selectedTags.includes(tagId)) {
      setSelectedTags(selectedTags.filter(id => id !== tagId));
    } else {
      setSelectedTags([...selectedTags, tagId]);
    }
  };
  
  const handleImageClick = (image) => {
    setSelectedImage(image);
    onOpen();
  };
  
  const handleEditTags = async (imageId, newTags) => {
    try {
      // Validate inputs
      if (!imageId) {
        throw new Error('Image ID is required');
      }
      
      // Get the image to update
      const imageToUpdate = images.find(img => img.id === imageId);
      if (!imageToUpdate) {
        throw new Error('Image not found');
      }
      
      // Update local state only (no DB update for tags)
      setImages(images.map(img => 
        img.id === imageId ? { ...img, tags: newTags } : img
      ));
      
      if (selectedImage && selectedImage.id === imageId) {
        setSelectedImage({ ...selectedImage, tags: newTags });
      }
      
      toast({
        title: 'Tags updated',
        description: 'Tags updated in memory only',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
    } catch (error) {
      console.error('Error updating tags:', error);
      toast({
        title: 'Update failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };
  
  const openTagEditor = (image) => {
    setSelectedImage(image);
    setEditingTags(image.tags || []);
    setIsTagModalOpen.on();
  };
  
  const handleSaveEditTags = async () => {
    if (selectedImage) {
      await handleEditTags(selectedImage.id, editingTags);
      setIsTagModalOpen.off();
    }
  };
  
  // Helper function to get image URL
  const getImageUrl = (image) => {
    if (!image) return '';
    
    // If it's a local image (blob URL)
    if (image.status === 'local' && image.image_url && image.image_url.startsWith('blob:')) {
      return image.image_url;
    }
    
    // For database images
    return image.image_url || image.file_url || '';
  };
  
  // Function to generate a secure approval link
  const generateApprovalLink = async () => {
    try {
      setSendingApproval(true);
      
      // Create a unique approval ID
      const approvalId = crypto.randomUUID();
      
      // Store the approval record in the database
      const { data, error } = await supabase
        .from('image_approvals')
        .insert({
          id: approvalId,
          user_id: userId,
          event_id: eventId,
          created_by: (await supabase.auth.getSession()).data.session?.user.id,
          status: 'pending',
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days expiry
        });
      
      if (error) throw error;
      
      // Update status of images to mark them as pending approval
      const imageIds = images.map(img => img.id);
      if (imageIds.length > 0) {
        const { error: updateError } = await supabase
          .from('user_uploads')
          .update({ approval_status: 'pending', approval_id: approvalId })
          .in('id', imageIds);
          
        if (updateError) {
          console.error('Error updating image approval status:', updateError);
        }
      }
      
      // Generate the link
      const approvalUrl = `${window.location.origin}/approve/${approvalId}`;
      setApprovalLink(approvalUrl);
      
      // Show the approval modal
      setIsApprovalModalOpen.on();
      
      // Track approval status locally
      let newApprovalStatus = {};
      images.forEach(img => {
        newApprovalStatus[img.id] = 'pending';
      });
      setApprovalStatus(newApprovalStatus);
      
      // Notify user
      toast({
        title: 'Approval link generated',
        description: 'You can now share this link with the client for image approval',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      // Send email to user (if available) with approval link
      if (user && user.email) {
        try {
          // You may need to implement or use an email service here
          // For example, using Supabase Edge Functions
          const { error: emailError } = await supabase.functions.invoke('send-approval-email', {
            body: { 
              email: user.email,
              name: user.first_name + ' ' + user.last_name,
              approvalUrl,
              eventName: event?.name || 'Your event',
              imageCount: images.length
            }
          });
          
          if (emailError) {
            console.error('Error sending email:', emailError);
          } else {
            toast({
              title: 'Email sent',
              description: `Approval link has been emailed to ${user.email}`,
              status: 'info',
              duration: 3000,
              isClosable: true,
            });
          }
        } catch (emailError) {
          console.error('Failed to send email:', emailError);
        }
      }
      
    } catch (error) {
      console.error('Error generating approval link:', error);
      toast({
        title: 'Error creating approval link',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSendingApproval(false);
    }
  };
  
  // Function to copy approval link to clipboard
  const copyApprovalLink = () => {
    navigator.clipboard.writeText(approvalLink);
    toast({
      title: 'Link copied',
      description: 'Approval link copied to clipboard',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  // Function to fetch current approval status
  const fetchApprovalStatus = async () => {
    try {
      // Get the approval status for images in this event for this user
      const { data, error } = await supabase
        .from('user_uploads')
        .select('id, approval_status')
        .eq('user_id', userId)
        .eq('upload_type', 'event_upload:' + eventId);
        
      if (error) throw error;
      
      if (data && data.length > 0) {
        let newApprovalStatus = {};
        data.forEach(item => {
          newApprovalStatus[item.id] = item.approval_status || 'not_sent';
        });
        setApprovalStatus(newApprovalStatus);
      }
    } catch (error) {
      console.error('Error fetching approval status:', error);
    }
  };
  
  // Call fetchApprovalStatus when component loads
  useEffect(() => {
    if (userId && eventId) {
      fetchApprovalStatus();
    }
  }, [userId, eventId]);
  
  if (loading) {
    return (
      <Flex justify="center" align="center" height="80vh" width="100%">
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text ml={4} fontSize="xl">Loading...</Text>
      </Flex>
    );
  }
  
  // Debug auth info - will show in the UI temporarily
  const authDebug = authInfo ? (
    <Box bg="gray.100" p={2} mb={4} borderRadius="md" fontSize="sm">
      <Text fontWeight="bold">Auth Debug:</Text>
      <Text>Status: {authInfo.authenticated ? 'Authenticated' : 'Not Authenticated'}</Text>
      <Text>User ID: {authInfo.userId}</Text>
      <Text>Role: {authInfo.role}</Text>
      <Text>Email: {authInfo.email}</Text>
    </Box>
  ) : null;
  
  return (
    <Box className="user-images-upload-container" p={4}>
      {authDebug}
      <Box mb={6}>
        <Link to={'/dashboard/events/' + eventId}>
          <Button leftIcon={<FaArrowLeft />} variant="ghost" size="sm">
            Back to Event
          </Button>
        </Link>
      </Box>
      
      {user && event && (
        <>
          <Card 
            mb={6} 
            bg={headerBgColor} 
            color="white"
            borderRadius="lg"
            boxShadow="md"
            overflow="hidden"
          >
            <CardBody>
              <Flex justify="space-between" align="center" wrap={{ base: 'wrap', md: 'nowrap' }}>
                <Box>
                  <Heading size="md">Upload Images with Background Removal</Heading>
                  <HStack mt={2}>
                    <FaUser />
                    <Text fontWeight="bold">{user.first_name} {user.last_name}</Text>
                  </HStack>
                  <Text fontSize="sm" mt={1}>for {event.name}</Text>
                </Box>
                
                <HStack mt={{ base: 4, md: 0 }} spacing={4}>
                  <FormControl>
                    <FormLabel htmlFor="tag-select" mb={1} fontSize="xs">Image Tags (Optional)</FormLabel>
                    <HStack flexWrap="wrap" spacing={2}>
                      {availableTags.map(tag => (
                        <Tag 
                          key={tag.id}
                          size="sm"
                          borderRadius="full"
                          variant={selectedTags.includes(tag.id) ? "solid" : "outline"}
                          colorScheme={tag.color}
                          cursor="pointer"
                          onClick={() => handleTagSelect(tag.id)}
                        >
                          <TagLeftIcon boxSize="12px" as={selectedTags.includes(tag.id) ? FaCheck : FaTag} />
                          <TagLabel>{tag.label}</TagLabel>
                        </Tag>
                      ))}
                    </HStack>
                  </FormControl>
                </HStack>
              </Flex>
            </CardBody>
          </Card>
          
          {/* Staff only: Approval Button */}
          {/* TEMPORARY OVERRIDE: Removed isStaff check to always show button for testing */}
          {images.length > 0 && (
            <Card mb={6} borderRadius="lg" boxShadow="lg" bg="blue.100">
              <CardBody>
                <Flex direction={{ base: "column", md: "row" }} justifyContent="space-between" alignItems="center">
                  <Box mb={{ base: 4, md: 0 }}>
                    <Heading size="md" color="blue.700">Client Approval</Heading>
                    <Text color="gray.600" fontSize="sm" mt={1}>
                      Send these {images.length} images to the client for review and selection
                    </Text>
                  </Box>
                  
                  <Button
                    leftIcon={<FaCheck />}
                    colorScheme="blue"
                    size="lg"
                    fontSize="md"
                    fontWeight="bold"
                    px={8}
                    py={6}
                    onClick={generateApprovalLink}
                    isLoading={sendingApproval}
                    loadingText="Generating link..."
                    _hover={{
                      transform: "translateY(-2px)",
                      boxShadow: "lg"
                    }}
                  >
                    Send for Approval
                  </Button>
                </Flex>
              </CardBody>
            </Card>
          )}
          
          <Card
            mb={6}
            borderRadius="lg"
            boxShadow="md"
            borderWidth="1px"
            borderColor={borderColor}
            bg={bgColor}
          >
            <CardBody>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <input 
                    type="file" 
                    accept="image/*" 
                    multiple 
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    colorScheme="blue"
                    size="lg"
                    width="100%"
                    height="120px"
                    borderRadius="md"
                    variant="outline"
                    leftIcon={<FaMagic />}
                    isLoading={uploadLoading}
                    loadingText={'Processing... ' + uploadProgress + '%'}
                    _hover={{ bg: 'blue.50', borderColor: 'blue.500' }}
                    transition="all 0.3s ease"
                  >
                    {uploadLoading ? (
                      <VStack>
                        <Text>Processing... {uploadProgress}%</Text>
                        <Box w="100%" h="8px" bg="blue.100" borderRadius="full" overflow="hidden">
                          <Box w={uploadProgress + '%'} h="100%" bg="blue.500" />
                        </Box>
                      </VStack>
                    ) : (
                      <VStack>
                        <Text>Click or drag files here to upload</Text>
                        <Text fontSize="sm" color="gray.500">
                          Background will be automatically removed (JPEG, PNG, GIF up to 10MB)
                        </Text>
                      </VStack>
                    )}
                  </Button>
                </FormControl>
                
                {hasUploaded && (
                  <Alert status="success" variant="subtle" borderRadius="md">
                    <AlertIcon />
                    <Box flex="1">
                      <AlertTitle>Processing complete!</AlertTitle>
                      <AlertDescription>
                        Images have been uploaded with background removed.
                      </AlertDescription>
                    </Box>
                  </Alert>
                )}
              </VStack>
            </CardBody>
          </Card>
          
          <Tabs variant="enclosed" colorScheme="blue">
            <TabList>
              <Tab>Gallery ({images.length})</Tab>
              <Tab>Upload History</Tab>
              {isStaff && <Tab>Approval Status</Tab>}
            </TabList>
            
            <TabPanels>
              <TabPanel>
                {images.length === 0 ? (
                  <Box 
                    textAlign="center" 
                    py={10} 
                    bg={bgColor} 
                    borderRadius="md" 
                    borderWidth="1px"
                    borderColor={borderColor}
                  >
                    <FaImage size={40} style={{ margin: '0 auto', color: 'gray' }} />
                    <Heading size="md" mt={4} color="gray.500">No Images Yet</Heading>
                    <Text mt={2} color="gray.500">
                      Upload images for this user by clicking the upload button above.
                    </Text>
                  </Box>
                ) : (
                  <Grid 
                    templateColumns={{ 
                      base: "repeat(1, 1fr)", 
                      sm: "repeat(2, 1fr)", 
                      md: "repeat(3, 1fr)", 
                      lg: "repeat(4, 1fr)" 
                    }}
                    gap={4}
                  >
                    {images.map(image => (
                      <GridItem 
                        key={image.id} 
                        className="image-card"
                        bg={bgColor}
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor={borderColor}
                        overflow="hidden"
                        transition="all 0.2s ease"
                        _hover={{ 
                          transform: "translateY(-4px)", 
                          boxShadow: "lg",
                          borderColor: "blue.300" 
                        }}
                      >
                        <Box onClick={() => handleImageClick(image)} cursor="pointer" position="relative">
                          <Box position="relative" pb="100%" overflow="hidden">
                            <Image
                              src={getImageUrl(image)}
                              alt={'User image ' + image.id}
                              position="absolute"
                              top="0"
                              left="0"
                              width="100%"
                              height="100%"
                              objectFit="cover"
                              fallbackSrc="https://via.placeholder.com/300?text=Loading..."
                            />
                            
                            {/* Approval status badge */}
                            {approvalStatus[image.id] && (
                              <Badge
                                position="absolute"
                                top="8px"
                                right="8px"
                                colorScheme={
                                  approvalStatus[image.id] === 'approved' ? 'green' :
                                  approvalStatus[image.id] === 'rejected' ? 'red' :
                                  approvalStatus[image.id] === 'pending' ? 'yellow' : 'gray'
                                }
                                borderRadius="full"
                                px={2}
                                py={1}
                              >
                                {approvalStatus[image.id] === 'approved' ? 'Approved' :
                                 approvalStatus[image.id] === 'rejected' ? 'Rejected' :
                                 approvalStatus[image.id] === 'pending' ? 'Pending' : 'Not Sent'}
                              </Badge>
                            )}
                          </Box>
                        </Box>
                        
                        <Box p={2}>
                          <HStack wrap="wrap" spacing={1} mb={2}>
                            {image.tags && image.tags.length > 0 ? image.tags.map(tagId => {
                              const tag = availableTags.find(t => t.id === tagId);
                              return tag ? (
                                <Badge 
                                  key={tagId} 
                                  colorScheme={tag.color}
                                  borderRadius="full"
                                  px={2}
                                  py={0.5}
                                  fontSize="xs"
                                  mr={1}
                                  mb={1}
                                >
                                  {tag.label}
                                </Badge>
                              ) : null;
                            }) : (
                              <Text fontSize="xs" color="gray.500">No tags</Text>
                            )}
                          </HStack>
                          
                          <Flex justify="space-between" align="center">
                            <IconButton
                              aria-label="Edit tags"
                              icon={<FaTag />}
                              size="xs"
                              colorScheme="blue"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                openTagEditor(image);
                              }}
                            />
                            <Text fontSize="xs" color="gray.500">
                              {new Date(image.created_at).toLocaleDateString()}
                            </Text>
                            <IconButton
                              aria-label="Delete image"
                              icon={<FaTrash />}
                              size="xs"
                              colorScheme="red"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteImage(image.id);
                              }}
                            />
                          </Flex>
                        </Box>
                      </GridItem>
                    ))}
                  </Grid>
                )}
              </TabPanel>
              
              <TabPanel>
                <Box 
                  textAlign="center" 
                  py={10} 
                  bg={bgColor} 
                  borderRadius="md" 
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <Heading size="md" color="gray.500">Upload History</Heading>
                  <Text mt={2} color="gray.500">
                    Detailed upload history will be available here in a future update.
                  </Text>
                </Box>
              </TabPanel>
              
              {/* New Approval Status Tab */}
              {isStaff && (
                <TabPanel>
                  <Card borderRadius="md" boxShadow="sm">
                    <CardHeader>
                      <Heading size="md">Image Approval Status</Heading>
                    </CardHeader>
                    <CardBody>
                      {images.length === 0 ? (
                        <Text color="gray.500">No images to approve</Text>
                      ) : (
                        <>
                          <VStack align="stretch" spacing={4}>
                            {images.map(image => (
                              <Flex 
                                key={image.id} 
                                borderWidth="1px" 
                                borderRadius="md" 
                                p={3}
                                align="center"
                                justify="space-between"
                              >
                                <HStack>
                                  <Image 
                                    src={getImageUrl(image)} 
                                    boxSize="50px" 
                                    borderRadius="md"
                                    objectFit="cover"
                                  />
                                  <Text>
                                    {new Date(image.created_at).toLocaleDateString()}
                                  </Text>
                                </HStack>
                                
                                <Badge
                                  colorScheme={
                                    approvalStatus[image.id] === 'approved' ? 'green' :
                                    approvalStatus[image.id] === 'rejected' ? 'red' :
                                    approvalStatus[image.id] === 'pending' ? 'yellow' : 'gray'
                                  }
                                  borderRadius="full"
                                  px={3}
                                  py={1}
                                >
                                  {approvalStatus[image.id] === 'approved' ? 'Approved' :
                                   approvalStatus[image.id] === 'rejected' ? 'Rejected' :
                                   approvalStatus[image.id] === 'pending' ? 'Pending Approval' : 'Not Sent'}
                                </Badge>
                              </Flex>
                            ))}
                          </VStack>
                          
                          {!Object.values(approvalStatus).includes('pending') && 
                           !Object.values(approvalStatus).includes('approved') && (
                            <Button
                              mt={6}
                              colorScheme="blue"
                              leftIcon={<FaCheck />}
                              onClick={generateApprovalLink}
                              isLoading={sendingApproval}
                              width="full"
                            >
                              Send Images for Approval
                            </Button>
                          )}
                        </>
                      )}
                    </CardBody>
                  </Card>
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
          
          {/* Image Detail Modal */}
          <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                <Flex justify="space-between" align="center">
                  <Text>Image Details</Text>
                  <CloseButton onClick={onClose} />
                </Flex>
              </ModalHeader>
              
              <ModalBody>
                {selectedImage && (
                  <VStack spacing={4} align="stretch">
                    <Box borderRadius="md" overflow="hidden">
                      <Image
                        src={getImageUrl(selectedImage)}
                        alt="Selected image"
                        width="100%"
                        fallbackSrc="https://via.placeholder.com/500?text=Loading..."
                      />
                    </Box>
                    
                    <HStack wrap="wrap" spacing={2}>
                      <Text fontWeight="bold">Tags:</Text>
                      {selectedImage.tags && selectedImage.tags.length > 0 ? (
                        selectedImage.tags.map(tagId => {
                          const tag = availableTags.find(t => t.id === tagId);
                          return tag ? (
                            <Badge 
                              key={tagId} 
                              colorScheme={tag.color}
                              borderRadius="full"
                              px={2}
                              py={1}
                            >
                              {tag.label}
                            </Badge>
                          ) : null;
                        })
                      ) : (
                        <Text color="gray.500">No tags assigned</Text>
                      )}
                      
                      <IconButton
                        aria-label="Edit tags"
                        icon={<FaEdit />}
                        size="xs"
                        ml={2}
                        onClick={() => openTagEditor(selectedImage)}
                      />
                    </HStack>
                    
                    <Divider />
                    
                    <Text fontSize="sm" color="gray.500">
                      Uploaded on {new Date(selectedImage.created_at).toLocaleString()}
                    </Text>
                  </VStack>
                )}
              </ModalBody>
              
              <ModalFooter>
                <Button colorScheme="blue" mr={3} onClick={onClose}>
                  Close
                </Button>
                {selectedImage && (
                  <Button 
                    colorScheme="red" 
                    variant="outline"
                    leftIcon={<FaTrash />}
                    onClick={() => {
                      handleDeleteImage(selectedImage.id);
                      onClose();
                    }}
                  >
                    Delete
                  </Button>
                )}
              </ModalFooter>
            </ModalContent>
          </Modal>
          
          {/* Tag Editing Modal */}
          <Modal isOpen={isTagModalOpen} onClose={setIsTagModalOpen.off} size="sm">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Edit Tags</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack align="stretch" spacing={3}>
                  {availableTags.map(tag => (
                    <Checkbox 
                      key={tag.id}
                      isChecked={editingTags.includes(tag.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setEditingTags([...editingTags, tag.id]);
                        } else {
                          setEditingTags(editingTags.filter(id => id !== tag.id));
                        }
                      }}
                      colorScheme={tag.color}
                    >
                      <HStack>
                        <Badge colorScheme={tag.color}>{tag.label}</Badge>
                      </HStack>
                    </Checkbox>
                  ))}
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button
                  colorScheme="blue"
                  mr={3}
                  onClick={handleSaveEditTags}
                >
                  Save
                </Button>
                <Button variant="ghost" onClick={setIsTagModalOpen.off}>
                  Cancel
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
          
          {/* New Approval Link Modal */}
          <Modal isOpen={isApprovalModalOpen} onClose={setIsApprovalModalOpen.off} size="md">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Approval Link Generated</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4} align="stretch">
                  <Alert status="success" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>Success!</AlertTitle>
                      <AlertDescription>
                        Approval link has been generated for {images.length} images
                      </AlertDescription>
                    </Box>
                  </Alert>
                  
                  <Box
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    bg="gray.50"
                    fontSize="sm"
                    position="relative"
                  >
                    <Text fontWeight="bold" mb={1}>Share this link with client:</Text>
                    <Text isTruncated>{approvalLink}</Text>
                    <IconButton
                      icon={<FaCheck />}
                      aria-label="Copy link"
                      size="xs"
                      position="absolute"
                      top="8px"
                      right="8px"
                      onClick={copyApprovalLink}
                    />
                  </Box>
                  
                  <Text fontWeight="bold">What happens next?</Text>
                  <Text fontSize="sm">
                    1. Share this link with your client<br />
                    2. They'll be able to view and approve/reject each image<br />
                    3. You'll receive notifications when the client completes the review
                  </Text>
                  
                  <Text fontSize="sm" color="gray.500">
                    This link will expire in 7 days. You can always generate a new one.
                  </Text>
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button colorScheme="blue" mr={3} onClick={copyApprovalLink}>
                  Copy Link
                </Button>
                <Button variant="ghost" onClick={setIsApprovalModalOpen.off}>
                  Close
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </>
      )}
    </Box>
  );
};

export default UserImagesUpload; 